/**
 * Appliance reservation, scene-object emission, and NKBA landing-clearance
 * warnings for manually-drawn cabinet runs. Pure. See
 * claude-docs/2026-06-13-manual-cabinet-runs-design.md §4–§5.
 */

const {
  getWallAxes,
  applianceCenterFromWallParam,
  worldToWallParam,
} = require('../kitchen-assembly/wallMath');
const {
  buildLowerCabinetForSegment,
} = require('../kitchen-assembly/buildLowerCabinet');
const {
  APPLIANCE_FOOTPRINTS,
} = require('../kitchen-assembly/candidates/applianceRequirements');
const {
  landingRequirementFor,
} = require('../kitchen-assembly/landingRequirements');
const {
  OPENING_BUFFER,
  FRIDGE_GAP_ABOVE,
} = require('../kitchen-assembly/constants');
const {
  buildVentHood,
  buildFloatingVentHood,
} = require('../kitchen-assembly/buildVentHood');
const { buildFarmhouseColumns } = require('../kitchen-assembly/sinkSeat');
const {
  buildFridgeSurround,
} = require('../kitchen-assembly/buildFridgeSurround');
const { profileOf, runBodyHeight, counterTopOffsetFor } = require('./profiles');
const { familyOf } = require('./families');
const { stampRollOuts } = require('./rollOuts');
const { chosenSinkBayFront, stampSinkDrawers } = require('./sinkUnits');
const { isVanityAssemblyGroupId } = require('../vanity-assembly/fixtureGroup');
const {
  fridgeColumnPanels,
  fridgeIsBuiltIn,
  fridgeColumnDepth,
  overFridgeCabinetHeightIn,
} = require('./fridgeColumn');
const {
  buildUpperCabinetForSegment,
} = require('../kitchen-assembly/buildUpperCabinet');
const { segmentFillBounds } = require('./corners');
const {
  governingOverride,
  applyBayType,
  effectiveAppearance,
  applyAppearanceBundle,
} = require('./bayOverrides');

const IN = 0.0254;

// A fixed-size install product within 1/4" of its appliance is a match.
const INSTALL_WIDTH_EPS = 0.00635;
const FIT_EPS = 1e-9;

// Footprint defaults, the bay model and the legacy-anchor adapters live in
// bays.js; re-exported below so existing importers keep working.
const {
  FOOTPRINT_BY_TYPE,
  footprintFor,
  footprintForAppliance,
  applianceBays,
  legacyApplianceFromBay,
} = require('./bays');
const { reuseInstallPiece } = require('./applianceInstall');
const { woodHoodUnits } = require('./woodHoods');
const { buildFridgeHeader } = require('./fridgeHeader');
const COUNTER_BREAK_TYPES = new Set([
  // No countertop runs over a pantry.
  'tallUnit',
  'range',
  'stove',
  'fridge',
  'refrigerator',
  // `cooktop` is deliberately ABSENT: a drop-in is cut INTO the counter, so
  // the slab runs over it exactly as it runs over a sink or a dishwasher.
]);
const SCENE_TYPE = {
  dishwasher: 'dishwasher',
  range: 'range',
  stove: 'range',
  cooktop: 'cooktop',
  fridge: 'refrigerator',
  refrigerator: 'refrigerator',
};
const SCENE_LABEL = {
  dishwasher: 'Dishwasher',
  range: 'Range',
  stove: 'Range',
  cooktop: 'Cooktop',
  fridge: 'Refrigerator',
  refrigerator: 'Refrigerator',
};

function isCounterBreak(type) {
  return COUNTER_BREAK_TYPES.has(type);
}

/**
 * Appliance reservations (param ranges) on a segment, sorted by start — the
 * bays' stored anchors, UNclamped. Clamping is the packer's job
 * (`planSubSpanSequence` fits every hard piece into the REAL corner-trimmed
 * fill zone and reports what it moved); doing it here too, against the
 * default corner reservation, produced a second, disagreeing answer that
 * the packer could never see. `clamped` is kept on the shape (always false)
 * for readers that still branch on it.
 */
function reservationsForSegment(run, segIndex) {
  const res = [];
  for (const bay of applianceBays(run, segIndex)) {
    const a = legacyApplianceFromBay(bay);
    const fp = footprintForAppliance(a);
    const half = fp.width / 2;
    const center = a.centerParam;
    const clamped = false;
    res.push({
      appliance: a,
      bayId: bay.id,
      bay,
      type: a.type,
      centerParam: center,
      lo: center - half,
      hi: center + half,
      width: fp.width,
      clamped,
    });
  }
  res.sort((p, q) => p.lo - q.lo);
  return res;
}

/**
 * Param ranges on the segment's wall that an appliance of `type` must avoid:
 * SAME-WALL openings it would rise in front of, each widened by OPENING_BUFFER.
 * An opening only blocks when the appliance's top edge sits above its sill — so
 * a counter-height sink/DW/range tucks under a ≥36" window the way a base
 * cabinet does, while a fridge (or anything reaching below the sill) is blocked,
 * and a door/floor-opening (~0 sill) blocks everything. Adjacent-wall openings
 * are a separate (tall-item) warning, not handled here. Pure.
 */
function blockingOpeningRangesForAppliance(run, segIndex, type, ctx) {
  const seg = (run.segments || [])[segIndex];
  if (!seg || !seg.wall) return [];
  const wall = seg.wall;
  // This run's own appliance height, for the same reason the width is: a
  // vanity sink base tops out at 32" where a kitchen's is 34.5", and the test
  // below is exactly "does the appliance rise above this sill".
  const applianceTopY =
    (ctx.floorY || 0) + footprintForAppliance({ type }, profileOf(run)).height;
  const ranges = [];
  for (const o of ctx.openings || []) {
    if ((o.parentWallIds || [])[0] !== wall.id) continue;
    if (!o.center || !o.dimensions) continue;
    const sillY = o.center.y - (o.dimensions.y || 0) / 2;
    if (sillY >= applianceTopY - FIT_EPS) continue; // appliance fits under it
    const p = worldToWallParam(wall, o.center);
    const half = (o.dimensions.x || 0) / 2 + OPENING_BUFFER;
    ranges.push({ lo: p - half, hi: p + half });
  }
  return ranges;
}

/**
 * Intervals where an appliance of `type` may be CENTRED on segment `segIndex`:
 * the corner-trimmed fill span minus same-wall openings and the existing
 * appliances, each remaining gap shrunk by half the footprint so the whole
 * appliance fits. The candidate must NOT already be in `run.appliances`. Pure.
 */
function applianceCenterIntervals(run, segIndex, type, ctx) {
  // The footprint THIS RUN reserves, not the kitchen table's. A vanity's sink
  // base is 30" (`VANITY_SINK_BASE_WIDTH`) where a kitchen's is 36", and this
  // asked the bare table: dropping a sink on a 35.8" vanity run was refused
  // with "Not enough clear space", on a run that fits a vanity sink base with
  // 5.8" to spare. `footprintForAppliance` is the profile-aware reader the
  // fill already uses for the same question — found in the browser, because
  // only a DROP asks it.
  const fp = footprintForAppliance({ type }, profileOf(run));
  const half = fp.width / 2;
  // The span the FILL packs when the caller has it (`ctx.fillBounds`, the
  // segment's zone): its corners as the run built them. The default reserves
  // a whole 36" corner box at every corner end, and a blind corner whose body
  // stands on the OTHER wall takes only that leg's depth (and a reveal) from
  // this one — 27", not 36", on Cabin, 2428 Lore Rd, where a 36" sink base
  // beside it then "fit nowhere" and a drag that cannot clamp slid it over
  // the dishwasher (2026-10-26).
  const { lo, hi } =
    ctx.fillBounds || segmentFillBounds(run.segments, segIndex);
  const obstacles = [
    ...blockingOpeningRangesForAppliance(run, segIndex, type, ctx),
    ...reservationsForSegment(run, segIndex).map((r) => ({
      lo: r.lo,
      hi: r.hi,
    })),
  ]
    .filter((o) => o.hi > lo + FIT_EPS && o.lo < hi - FIT_EPS)
    .sort((a, b) => a.lo - b.lo);

  const intervals = [];
  let cursor = lo;
  for (const o of obstacles) {
    const oLo = Math.max(lo, o.lo);
    const oHi = Math.min(hi, o.hi);
    if (oHi <= cursor + FIT_EPS) continue; // behind / overlapping the cursor
    if (oLo - cursor >= fp.width - FIT_EPS) {
      intervals.push([cursor + half, oLo - half]);
    }
    cursor = oHi;
  }
  if (hi - cursor >= fp.width - FIT_EPS) {
    intervals.push([cursor + half, hi - half]);
  }
  return intervals;
}

/**
 * The valid appliance centre nearest `rawCenter`, or `null` if the appliance
 * can't fit anywhere clear on the segment. Lets the drop preview/placement
 * SLIDE the appliance out of openings (and off the run's ends / other
 * appliances) instead of ever sitting in a prohibited spot — the same feel as
 * the corner-cabinet clamp. Pure.
 */
function clampApplianceCenter(run, segIndex, type, rawCenter, ctx) {
  const intervals = applianceCenterIntervals(run, segIndex, type, ctx);
  if (intervals.length === 0) return null;
  let best = null;
  let bestDist = Infinity;
  for (const [a, b] of intervals) {
    const c = Math.min(Math.max(rawCenter, a), b);
    const d = Math.abs(c - rawCenter);
    if (d < bestDist) {
      bestDist = d;
      best = c;
    }
  }
  return best;
}

const RANGE_TYPES = new Set(['range', 'stove', 'cooktop']);

/**
 * How high off the floor the surface being cooked on stands — what a hood's
 * clearance is measured up from: the run's counter top, for a range and a
 * cooktop alike.
 *
 * Never the appliance's own box. A COOKTOP's footprint height is the thickness
 * of the unit (3/4"), and reading it hung its hood 24" above a point 3/4" off
 * the FLOOR — inside the base cabinets (found 2026-09-20 on the corpus's one
 * cooktop run). A RANGE's box is its catalog model's once styled (#1754), and
 * that includes the raised back and control panel: 6 of the 7 dev ranges stand
 * over 37.5", and the GE on Golden West, Temple City / Scan 1 is 47.25", so
 * every edit of its run hung the hood 11.25" too high (2026-10-21). A range is
 * cooked on at counter height. Generated kitchens never showed either: their
 * placements carry the standard 36".
 */
function cookingSurfaceHeight(run) {
  return counterTopOffsetFor(run);
}
const FRIDGE_TYPES = new Set(['fridge', 'refrigerator']);

/**
 * Vent hood above a range/stove. Floating (chimney to the ceiling) on a virtual
 * island wall, wall-mounted otherwise. Mirrors the kitchen generator's hood.
 * Returns the hood object (tagged to the run) or null.
 */
function buildRangeHood(run, r, fp, wall, ctx) {
  const placement = {
    applianceId: r.appliance.id,
    wallParam: r.centerParam,
    width: fp.width,
    height: cookingSurfaceHeight(run),
    depth: fp.depth,
    // the range installation's group, so the hood travels with the range
    ...(r.appliance.fixtureGroupId
      ? { fixtureGroupId: r.appliance.fixtureGroupId }
      : {}),
  };
  const hood = wall.isVirtual
    ? buildFloatingVentHood(
        placement,
        wall,
        ctx.floorY,
        ctx.ceilingY,
        ctx.ceilingId
      )
    : // Up to the run's upper line (`ctx.upperTopY`, resolved once in
      // `fillRun`), its chimney cover on to a stacked row's top
      // (`ctx.hoodChimneyTopY`).
      buildVentHood(placement, wall, ctx.floorY, {
        topY: ctx.upperTopY,
        ...(ctx.hoodChimneyTopY != null
          ? { chimneyTopY: ctx.hoodChimneyTopY }
          : {}),
      });
  if (!hood) return null;
  hood.addedBy = 'manual-cabinet-run';
  hood.runId = run.id;
  return hood;
}

/**
 * Fridge install: a full-height surround (fridge width × fridge depth) plus an
 * over-fridge cabinet above the fridge — or, with no room for one, a header
 * panel closing the gap (`fridgeHeader.js`). Both top out at the run's column line
 * (ctx.columnTopY — the uppers' line, or a stacked row's top:
 * `fridgeColumn.fridgeColumnLineY`) when known — so the unit lines up with the
 * cabinets beside it instead of running to the ceiling — capped to the
 * ceiling and never below the fridge itself; without an upper line they run
 * to the ceiling. The fridge box itself
 * is emitted separately. Needs ctx.ceilingY — returns [] without it. Both pieces
 * take the run/room appearance so they match the rest of the run. Mirrors the
 * kitchen generator's fridge treatment.
 */
function buildFridgeInstall(run, r, fp, wall, ctx) {
  const { ceilingY, floorY, columnTopY } = ctx;
  if (typeof ceilingY !== 'number') return [];
  const a = r.appliance;
  const groupId = `manualFridgeInstall_${a.id}`;

  // Top of the fridge cabinetry: the run's column line so it aligns with the
  // cabinets beside it, clamped to the ceiling and never below the fridge box.
  const fridgeTop = floorY + (fp.height || 0);
  const topY =
    typeof columnTopY === 'number'
      ? Math.min(ceilingY, Math.max(columnTopY, fridgeTop))
      : ceilingY;

  const appearance = effectiveAppearance({ run, ctx });

  const placement = {
    applianceId: a.id,
    range: { start: r.lo, end: r.hi },
    width: fp.width,
    height: fp.height,
    depth: fp.depth,
  };
  // A vendor family's column is its own end panels — Venus: 1½" thick, 24"
  // deep, flush with the bridge below — else our ½" surround, as deep as the
  // fridge's type asks (`fridgeColumn.js`). The cabinet over the fridge is
  // the column's depth too.
  const family = familyOf(run);
  const panels = fridgeColumnPanels(run);
  const columnDepth = fridgeColumnDepth(family, fp.depth);
  const surround = buildFridgeSurround({
    placement,
    wall,
    floorY,
    ceilingY: topY,
    fixtureGroupId: groupId,
    sidePanelThickness: panels.thickness,
    depth: columnDepth,
  });
  if (!surround) return [];
  surround.addedBy = 'manual-cabinet-run';
  surround.runId = run.id;
  applyAppearanceBundle(surround, appearance);
  const out = [surround];

  // Over-fridge cabinet, top on topY: the room over the fridge (and its gap)
  // filled, in a line with no size catalog — never with less than a 12"
  // cabinet; in one with sizes, the tallest 24"-deep bridge it sells that fits
  // (design 2026-10-05, D3). Or none (`overFridgeCabinetHeightIn`, the rule
  // `style_kitchen` shares). The surround is a panel, built as before.
  const room = topY - (floorY + (fp.height || 0) + FRIDGE_GAP_ABOVE);
  const heightIn = overFridgeCabinetHeightIn(
    family,
    (r.hi - r.lo) / IN,
    room / IN
  );
  if (heightIn != null) {
    const overHeight = heightIn * IN;
    const overBottomY = topY - overHeight;
    const overDepth = columnDepth;
    const overCabinet = buildUpperCabinetForSegment(
      wall,
      { start: r.lo, end: r.hi },
      floorY,
      {
        depth: overDepth,
        height: overHeight,
        bottomY: overBottomY,
        label: 'Over-Fridge Cabinet',
        handleAtBottom: true,
      }
    );
    if (overCabinet) {
      overCabinet.addedBy = 'manual-cabinet-run';
      overCabinet.runId = run.id;
      overCabinet.fixtureGroupId = groupId;
      applyAppearanceBundle(overCabinet, appearance);
      out.push(overCabinet);
    }
  } else {
    // no cabinet that short: a ¾" header panel closes the gap
    // (`fridgeHeader.js`)
    const header = buildFridgeHeader(
      wall,
      { start: r.lo, end: r.hi },
      { bottomY: topY - room, topY, depth: columnDepth }
    );
    if (header) {
      header.id = `fridge_header_${a.id}`;
      header.addedBy = 'manual-cabinet-run';
      header.runId = run.id;
      header.fixtureGroupId = groupId;
      applyAppearanceBundle(header, appearance);
      out.push(header);
    }
  }
  return out;
}

/**
 * Emit appliance scene objects + the sink-base cabinets + the counter
 * items appliances contribute (sink via its base, DW as a parent-less
 * counter item). Range adds a vent hood; fridge adds a surround +
 * over-fridge cabinet. A cooktop, like a sink, is HOSTED: the run builds the
 * base cabinet it is cut into and the counter runs over it. Returns
 * { objects, sinkBases, cooktopBases, counterItems }.
 */
function buildApplianceObjects(run, ctx) {
  const floorY = ctx.floorY;
  // Install pieces that are a fixed-size product no longer matching their
  // appliance — reported, never stretched to fit.
  const installMismatches = [];
  // wood hoods this line sells none of, turned back to metal (`reuseInstallPiece`)
  const droppedWoodHoods = [];
  const profile = profileOf(run);
  const objects = [];
  const sinkBases = [];
  const cooktopBases = [];
  const counterItems = [];
  // Param spans that already carry, or are going to carry, a HOSTED base.
  //
  // Two hosted bays cannot share a span in a real kitchen, but nothing stops
  // the data claiming they do (a scan that put a sink and a hob at one point),
  // and `buildLowerCabinetForSegment` keys a cabinet's id on its span START —
  // so a second base over the first comes back under the SAME id, which is a
  // corrupt room, not a cosmetic overlap. The SINK's base wins: it carries the
  // basin, the faucet and the fixture group, and a cooktop over a sink base
  // still has a cabinet under it.
  const hostedSpans = [];
  const overlapsHosted = (wallId, range) =>
    hostedSpans.some(
      (h) =>
        h.wallId === wallId &&
        Math.min(h.end, range.end) - Math.max(h.start, range.start) > FIT_EPS
    );
  // { cabinet, wall, range, applianceId } per sink — fillRun builds the basin +
  // faucet from these once the run countertop exists.
  const sinkAssociations = [];

  run.segments.forEach((seg, segIndex) => {
    const axes = getWallAxes(seg.wall);
    if (!axes) return;
    const ry = axes.wallRotationY;

    // Claimed ahead of the loop, not as we go: the reservations are ordered by
    // param, so a cooktop sharing a sink's anchor can be reached FIRST and
    // would otherwise build its base before the sink ever claims the span.
    for (const x of reservationsForSegment(run, segIndex)) {
      if (x.appliance?.type === 'sink') {
        hostedSpans.push({ wallId: seg.wall.id, start: x.lo, end: x.hi });
      }
    }

    for (const r of reservationsForSegment(run, segIndex)) {
      const a = r.appliance;
      const fp = footprintForAppliance(a);
      const range = { start: r.lo, end: r.hi };

      if (a.type === 'sink') {
        // At the RUN's depth, not the standard one. `emitBay` builds every
        // ordinary bay from `ctx.cabinetDepth`; the sink base ignored it, so on
        // a run at any other depth the base came out 24" among 25" neighbours.
        // `groupIntoRuns` then broke the counter right there, leaving the sink
        // base bare and the basin parented to the fragment beside it.
        // Height/label come from the run's dimension profile, same as emitBay.
        const { cabinet } = buildLowerCabinetForSegment(
          seg.wall,
          range,
          floorY,
          {
            depth: ctx.cabinetDepth ?? profile.depth,
            height: runBodyHeight(run),
            label: profile.lowerLabel,
          }
        );
        if (cabinet) {
          // Sink base is its own bay type: a fixed apron panel over doors. It
          // defaults to 'sink' but honors a per-bay type override (so the user
          // can switch it to drawers/doors from the panel) — on a generic
          // line. A line with a size catalog builds its own sink cabinet here
          // whatever an older record asks (`sinkUnits.js`): Venus makes none
          // that is drawers only.
          const ov = governingOverride(run, segIndex, range);
          const chosenFront = chosenSinkBayFront(run, ov);
          applyBayType(cabinet, chosenFront || 'sink');
          // A FARMHOUSE basin brings its own apron, and that apron stands in
          // the door plane: the plain `sink` front would draw a false apron
          // strip right behind the real one. Give the base the farmhouse
          // front instead (one column, ordinary doors starting below the
          // apron) — the same builder post-processing uses, so the scan path
          // and a browser re-solve produce the same fronts. Skipped when the
          // user has overridden the bay's type: that choice is theirs.
          if (!chosenFront) {
            const basin = (ctx.existingObjects || []).find(
              (o) => o?.id === a.id
            );
            const farmhouse = basin
              ? buildFarmhouseColumns(cabinet, basin)
              : null;
            if (farmhouse) cabinet.columns = farmhouse;
          }
          // Carry the governing override's id so a later edit UPDATES the same
          // override instead of stacking a new one (without this, switching the
          // sink to Drawer then back to Sink left the old drawer override in
          // place — it couldn't change back).
          if (ov) cabinet.overrideId = ov.id;
          cabinet.addedBy = 'manual-cabinet-run';
          cabinet.runId = run.id;
          cabinet.applianceId = a.id;
          // A vanity line's DOUBLE-BOWL unit is one bay holding two basins
          // (`vanitySinkBay.vanitySinkPlan`). The box says so the way a
          // vanity body always has, `sinkCount`, which is what the estimate
          // reads to tell Venus's 60" double from its 60" single. An ordinary
          // sink bay carries none and its box is untouched.
          if (r.bay?.sinkCount > 1) cabinet.sinkCount = r.bay.sinkCount;
          // Drawers to one side of the basin (Venus's VSD…L / R), where the
          // record asks and the line makes that cabinet at this width.
          stampSinkDrawers(cabinet, ov, familyOf(run), {
            profileId: profile.id,
          });
          // Group the cabinet with its basin + faucet so they don't collide
          // with each other. JOIN the installation's existing group when the
          // bay knows it — minting `manualSink_` here split the generator's
          // sink install in two and, worse, REPLACED the group's anchor
          // cabinet: the sink → faucet links living on its
          // `groupCustomization` died with it on the first re-solve (user-
          // found 2026-08-30). The previous anchor's record is carried over.
          cabinet.fixtureGroupId = a.fixtureGroupId || `manualSink_${a.id}`;
          const prevAnchor = (ctx.existingObjects || []).find(
            (o) =>
              o?.groupCustomization &&
              o.type === 'lower-cabinet' &&
              (o.bayId === r.bayId ||
                (a.fixtureGroupId && o.fixtureGroupId === a.fixtureGroupId))
          );
          if (prevAnchor?.groupCustomization) {
            cabinet.groupCustomization = prevAnchor.groupCustomization;
          }
          // A VANITY's body is stamped (`groupAnchor`), as `assembleVanity`
          // stamps the cabinet it builds and `getVanityCabinet` reads. The
          // fill replaces that cabinet and dropped the stamp with it, leaving
          // the body to array order. Once per group: a double whose bowls
          // each get a base has two sink bases here, and one body.
          if (
            isVanityAssemblyGroupId(cabinet.fixtureGroupId) &&
            !sinkBases.some((b) => b.fixtureGroupId === cabinet.fixtureGroupId)
          ) {
            cabinet.groupAnchor = true;
          }
          sinkBases.push(cabinet);
          counterItems.push({
            wallId: seg.wall.id,
            range,
            type: 'lower-cabinet',
            id: cabinet.id,
            // Carry the base's own depth, exactly as `emitBay` does. A missing
            // one defaults to the standard 24" inside `groupIntoRuns`.
            depth: cabinet.dimensions?.z,
          });
          sinkAssociations.push({
            cabinet,
            wall: seg.wall,
            range,
            applianceId: a.id,
            bayId: r.bayId,
          });
          cabinet.bayId = r.bayId;
        }
        continue;
      }

      const center = applianceCenterFromWallParam(
        seg.wall,
        r.centerParam,
        fp.depth,
        floorY,
        fp.height
      );
      // A tall unit is ANCHORED, not built: the scanned object stays (it owns a
      // height the engine has no business inventing) and is reseated onto this
      // reservation. Emitting one here would duplicate it.
      if (a.type === 'tallUnit') continue;

      // A COOKTOP is hosted: it is cut into the counter over a base cabinet
      // the run builds, so its bay is filled rather than left as a void. The
      // scene object is still emitted (the generated path has no cooktop of
      // its own); the scan path discards it and keeps the measured one.
      if (a.type === 'cooktop' && !overlapsHosted(seg.wall.id, range)) {
        const { cabinet } = buildLowerCabinetForSegment(
          seg.wall,
          range,
          floorY,
          {
            depth: ctx.cabinetDepth ?? profile.depth,
            height: runBodyHeight(run),
            label: profile.lowerLabel,
          }
        );
        if (cabinet) {
          const ov = governingOverride(run, segIndex, range);
          applyBayType(cabinet, (ov && ov.type) || 'standard');
          if (ov) cabinet.overrideId = ov.id;
          cabinet.label = 'Cooktop Base';
          cabinet.addedBy = 'manual-cabinet-run';
          cabinet.runId = run.id;
          // pots under the cooktop: its roll-outs ride the same record as
          // any bay's (`emitBay`)
          stampRollOuts(cabinet, ov, familyOf(run));
          cabinet.applianceId = a.id;
          cabinet.bayId = r.bayId;
          cooktopBases.push(cabinet);
          hostedSpans.push({ wallId: seg.wall.id, ...range });
          counterItems.push({
            wallId: seg.wall.id,
            range,
            type: 'lower-cabinet',
            id: cabinet.id,
            depth: cabinet.dimensions?.z,
          });
        }
      }

      const sceneType = SCENE_TYPE[a.type] || a.type;
      const objId =
        a.id || `${sceneType}_${segIndex}_${Math.round(a.centerParam * 1000)}`;
      // REUSE the incumbent (fillRun's `reuse` for the basin and faucet,
      // applied to the appliance): a manual run's appliance is emitted once
      // and then MOVED, so its model, dimensions, finish and customization
      // survive the re-solve. Only what the run owns — position, rotation,
      // wall, bay, run — is rewritten. Without this a model swap was
      // reverted by the next bay edit, appearance apply or wall move (#1754).
      // The bay was resolved from this same object (bayFootprint.js), so
      // `center` and the install pieces below already use its true size.
      const prior = (ctx.existingObjects || []).find((o) => o?.id === objId);
      const owned = {
        center,
        rotation: { x: 0, y: ry, z: 0 },
        parentWallIds: [seg.wall.id],
        bayId: r.bayId,
        addedBy: 'manual-cabinet-run',
        runId: run.id,
        lastModified: new Date().toISOString(),
      };
      const obj = prior
        ? {
            ...prior,
            ...owned,
            fixtureGroupId: prior.fixtureGroupId || `manualAppliance_${objId}`,
          }
        : {
            id: objId,
            type: sceneType,
            label: SCENE_LABEL[a.type] || sceneType,
            category: { [sceneType]: true },
            dimensions: { x: fp.width, y: fp.height, z: fp.depth },
            fixtureGroupId: `manualAppliance_${objId}`,
            ...owned,
          };
      objects.push(obj);

      // An install piece is re-emitted OVER its incumbent: the run owns where
      // it sits, never the product standing there. `reuseInstallPiece` keeps
      // a catalog GLB (and its finish) and refuses to stretch one the catalog
      // never said may be stretched — it re-seats it instead and reports the
      // mismatch, rather than silently distorting the mesh.
      const reuse = (piece) => {
        if (!piece) return null;
        const {
          piece: kept,
          resized,
          userSized,
          droppedHoodSku,
        } = reuseInstallPiece(piece, ctx.existingObjects, [obj], seg.wall, {
          woodHoods: woodHoodUnits(familyOf(run)).length > 0,
        });
        if (droppedHoodSku) droppedWoodHoods.push(droppedHoodSku);
        if (!resized) {
          const own = kept.dimensions?.x || 0;
          if (Math.abs(own - fp.width) > INSTALL_WIDTH_EPS) {
            installMismatches.push({
              objectId: kept.id,
              label: kept.label || kept.type,
              applianceLabel: SCENE_LABEL[a.type] || a.type,
              own,
              wanted: fp.width,
              userSized,
            });
          }
        }
        return kept;
      };

      if (a.type === 'dishwasher') {
        counterItems.push({
          wallId: seg.wall.id,
          range,
          type: 'dishwasher',
          id: obj.id,
          // The counter over it is the RUN's, at the run's depth — exactly as
          // the sink base's is (above). With none `groupIntoRuns` read it as
          // the standard 24", so on a run at any other depth (an island row
          // set to 26") the dishwasher was a slab group of its own: no slab at
          // all between two cabinets, a 2"-shallower one beside an end panel.
          depth: ctx.cabinetDepth ?? profile.depth,
        });
      } else if (RANGE_TYPES.has(a.type)) {
        // Range → a vent hood above the cooktop.
        const hood = reuse(buildRangeHood(run, r, fp, seg.wall, ctx));
        if (hood) objects.push(hood);
      } else if (FRIDGE_TYPES.has(a.type) && fridgeIsBuiltIn(r.bay)) {
        // Fridge → a full-height surround + an over-fridge cabinet. A
        // free-standing one gets neither: it stands as the kitchen had it.
        objects.push(
          ...buildFridgeInstall(run, r, fp, seg.wall, ctx)
            .map(reuse)
            .filter(Boolean)
        );
      }
    }
  });

  return {
    objects,
    sinkBases,
    cooktopBases,
    counterItems,
    sinkAssociations,
    installMismatches,
    droppedWoodHoods,
  };
}

/** Contiguous counter width on one side of appliance k (counter spans DW/sink). */
function landingOnSide(appls, k, segLo, segHi, direction) {
  let total = 0;
  if (direction < 0) {
    let edge = appls[k].lo;
    let j = k - 1;
    for (;;) {
      const prevHi = j >= 0 ? appls[j].hi : segLo;
      total += Math.max(0, edge - prevHi);
      if (j < 0 || isCounterBreak(appls[j].type)) break;
      total += appls[j].hi - appls[j].lo;
      edge = appls[j].lo;
      j -= 1;
    }
  } else {
    let edge = appls[k].hi;
    let j = k + 1;
    for (;;) {
      const nextLo = j < appls.length ? appls[j].lo : segHi;
      total += Math.max(0, nextLo - edge);
      if (j >= appls.length || isCounterBreak(appls[j].type)) break;
      total += appls[j].hi - appls[j].lo;
      edge = appls[j].hi;
      j += 1;
    }
  }
  return total;
}

function landingMessage(type, side, smaller, larger) {
  const minIn = Math.round(side.minWidth / IN);
  const haveIn = Math.round((side.which === 'larger' ? larger : smaller) / IN);
  const cap = type.charAt(0).toUpperCase() + type.slice(1);
  return `${cap} landing ${haveIn}" — NKBA suggests ${minIn}" on the ${side.which} side.`;
}

/** NKBA landing-clearance warnings (non-blocking guidance). */
function computeWarnings(run, ctx) {
  const warnings = [];
  run.segments.forEach((seg, segIndex) => {
    const appls = reservationsForSegment(run, segIndex);
    const segLo = Math.min(seg.startParam, seg.endParam);
    const segHi = Math.max(seg.startParam, seg.endParam);

    appls.forEach((r, k) => {
      if (r.clamped) {
        const cap = r.type.charAt(0).toUpperCase() + r.type.slice(1);
        warnings.push({
          applianceId: r.appliance.id,
          type: r.type,
          severity: 'warning',
          message: `${cap} moved to keep clear of the corner cabinet.`,
        });
      }
      // The run's PROFILE decides whether the standard applies at all — the
      // table is NKBA kitchen guidance and a vanity has no landing rule.
      const req = landingRequirementFor(r.type, profileOf(run).id);
      if (!req) return;
      const left = landingOnSide(appls, k, segLo, segHi, -1);
      const right = landingOnSide(appls, k, segLo, segHi, +1);
      const larger = Math.max(left, right);
      const smaller = Math.min(left, right);

      for (const side of req.sides) {
        let ok = true;
        let hard = false;
        if (side.which === 'larger') ok = larger >= side.minWidth - 1e-6;
        else if (side.which === 'smaller') ok = smaller >= side.minWidth - 1e-6;
        else if (side.which === 'handle') {
          ok = larger >= side.minWidth - 1e-6;
          if (side.hardRequireNonZero && larger <= 1e-6) {
            ok = false;
            hard = true;
          }
        }
        if (!ok) {
          warnings.push({
            applianceId: r.appliance.id,
            type: r.type,
            severity: hard ? 'error' : 'warning',
            message: landingMessage(r.type, side, smaller, larger),
          });
        }
      }
    });
  });
  return warnings;
}

module.exports = {
  reservationsForSegment,
  blockingOpeningRangesForAppliance,
  applianceCenterIntervals,
  clampApplianceCenter,
  buildApplianceObjects,
  computeWarnings,
  footprintFor,
  footprintForAppliance,
  isCounterBreak,
};
